import React, { useRef, useState } from 'react';
import { Box, Button, Checkbox, Chip, FormControl, IconButton, InputLabel, MenuItem, Select, TextField, Tooltip, Typography, Autocomplete } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { THEME_COLOR, checkboxStyle, datePickerSlotProps, inputLabelFocusedStyle, selectFocusedStyle } from '../../../theme/formStyles';
import { fmtRange } from '../projectSchedule.utils';
import { STEP_STATUS_OPTIONS, spocName, taskNameOptions } from './editorOptions';

const fieldSx = { '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: THEME_COLOR }, '& .MuiInputLabel-root.Mui-focused': { color: THEME_COLOR } };

// A task's step state from its sub-tasks (same rule as the server's deriveStepStatus, minus the date check)
export const taskState = (subs) => {
    const live = subs.filter(s => s.status !== 'Cancelled');
    if (!live.length) return 'upcoming';
    if (live.every(s => s.status === 'Completed')) return 'done';
    const open = live.filter(s => s.status !== 'Completed');
    if (open.every(s => s.status === 'On Hold')) return 'on_hold';
    if (live.some(s => s.status === 'In Progress' || s.status === 'Completed')) return 'now';
    return 'upcoming';
};
const STATE_CHIP = {
    done: { label: 'Done', color: '#15803d', bg: '#e8f7ee' },
    now: { label: 'In progress', color: THEME_COLOR, bg: '#e6f4f2' },
    on_hold: { label: 'On hold', color: '#8f6d1d', bg: '#fffdee' },
    upcoming: { label: 'Not started', color: '#64748b', bg: '#f1f5f9' },
};
export const taskRange = (subs) => {
    const dated = subs.filter(s => s.start_date);
    if (!dated.length) return 'No dates yet';
    const a = dated.map(s => s.start_date).sort()[0];
    const b = dated.map(s => s.end_date || s.start_date).sort().reverse()[0];
    return fmtRange(a, b);
};

// Two rows that shrink with the dialog (columns are minmax(0, …) so nothing spills into the Client / delete column):
//   wide:   [Step name ······][Status][＋ Sub-tasks]      phone: [Step name ······]
//           [Start][End][Or date text ······]                    [Start][End] [Date text ···] [Status][＋ Sub-tasks]
function SimpleStepFields({ item, error, onChange, onMakeTask }) {
    const d = (v) => (v ? dayjs(v) : null);
    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0,1fr))', md: 'repeat(4, minmax(0,1fr))' }, gap: 1, alignItems: 'start', flex: 1, minWidth: 0 }}>
            <TextField size="small" label="Step name *" value={item.label} error={!!error} helperText={error}
                onChange={(e) => onChange({ label: e.target.value })} inputProps={{ maxLength: 255 }} sx={{ ...fieldSx, gridColumn: { xs: '1 / -1', md: 'span 2' }, order: 0 }} />
            <FormControl size="small" sx={{ order: { xs: 5, md: 1 }, minWidth: 0 }}>
                <InputLabel sx={inputLabelFocusedStyle}>Status</InputLabel>
                <Select label="Status" value={item.status || 'upcoming'} onChange={(e) => onChange({ status: e.target.value })} sx={selectFocusedStyle} MenuProps={{ disableScrollLock: true }}>
                    {STEP_STATUS_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </Select>
            </FormControl>
            <Button size="small" variant="outlined" onClick={onMakeTask}
                sx={{ order: { xs: 6, md: 2 }, minWidth: 0, color: THEME_COLOR, borderStyle: 'dashed', borderColor: '#b9c3cd', textTransform: 'none', whiteSpace: 'nowrap', height: 40 }}>＋ Sub-tasks</Button>
            <Box sx={{ order: 3, minWidth: 0 }}>
                <DatePicker label="Start date" format="DD/MM/YYYY" value={d(item.start_date)}
                    onChange={(v) => { const s = v && v.isValid() ? v.format('YYYY-MM-DD') : ''; onChange({ start_date: s, end_date: s && (!item.end_date || item.end_date < s) ? s : item.end_date }); }}
                    slotProps={datePickerSlotProps({ size: 'small', fullWidth: true })} />
            </Box>
            <Box sx={{ order: 3, minWidth: 0 }}>
                <DatePicker label="End date" format="DD/MM/YYYY" value={d(item.end_date)} minDate={d(item.start_date) || undefined} disabled={!item.start_date}
                    onChange={(v) => onChange({ end_date: v && v.isValid() ? v.format('YYYY-MM-DD') : item.start_date })}
                    slotProps={datePickerSlotProps({ size: 'small', fullWidth: true })} />
            </Box>
            <TextField size="small" label={item.start_date ? 'Date note' : 'Or date text'} placeholder="e.g. After handover" value={item.date_label || ''}
                onChange={(e) => onChange({ date_label: e.target.value })} inputProps={{ maxLength: 100 }} sx={{ ...fieldSx, order: 4, minWidth: 0, gridColumn: 'span 2' }} />
        </Box>
    );
}

function TaskFields({ item, error, taskTags, onChange, onOpen }) {
    const st = STATE_CHIP[taskState(item.sub_tasks)];
    const done = item.sub_tasks.filter(s => s.status === 'Completed').length;
    return (
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Autocomplete freeSolo size="small" options={taskNameOptions(taskTags)} value={item.task_name}
                    onChange={(e, v) => onChange({ task_name: v || '' })} onInputChange={(e, v, reason) => { if (reason === 'input') onChange({ task_name: v }); }}
                    renderInput={(params) => <TextField {...params} label="Task Name *" error={!!error} helperText={error} sx={fieldSx} />}
                    sx={{ flex: '1 1 240px', minWidth: 200 }} />
                <Chip size="small" label={st.label} sx={{ fontWeight: 700, color: st.color, bgcolor: st.bg }} />
                <Typography sx={{ fontSize: '12.5px', color: '#6b7785' }}>{taskRange(item.sub_tasks)} · {done}/{item.sub_tasks.length} sub-tasks</Typography>
            </Box>
            <Box sx={{ mt: 1, borderTop: '1px solid #eef1f4', pt: 0.5 }}>
                {item.sub_tasks.map((s) => (
                    <Box key={s.key} onClick={() => onOpen(s.key)} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr auto', md: 'minmax(0,1.6fr) 150px minmax(0,1fr) auto' }, gap: 1, alignItems: 'center', px: 1, py: 0.6, borderRadius: 1.5, cursor: 'pointer', '&:hover': { bgcolor: '#f7fafc' } }}>
                        <Typography noWrap sx={{ fontSize: '13px', fontWeight: 500 }}>{s.sub_task_name || <em style={{ color: '#94a3b8' }}>Untitled sub-task</em>}</Typography>
                        <Typography noWrap sx={{ fontSize: '12px', color: '#6b7785', display: { xs: 'none', md: 'block' } }}>{s.start_date ? fmtRange(s.start_date, s.end_date || s.start_date) : 'No dates'}</Typography>
                        <Typography noWrap sx={{ fontSize: '12px', color: '#475569', display: { xs: 'none', md: 'block' } }}>{spocName(s.spoc) || 'Unassigned'}</Typography>
                        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#64748b' }}>{s.status}</Typography>
                    </Box>
                ))}
                {!item.sub_tasks.length && <Typography sx={{ fontSize: '12.5px', color: '#94a3b8', px: 1, py: 0.5 }}>No sub-tasks added yet.</Typography>}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <Button size="small" onClick={() => onOpen('new')} sx={{ color: THEME_COLOR, textTransform: 'none' }}>＋ Add sub-task</Button>
                    <Button size="small" onClick={() => onOpen(null)} sx={{ color: THEME_COLOR, textTransform: 'none' }}>Open task ›</Button>
                </Box>
            </Box>
        </Box>
    );
}

// The ordered timeline: simple steps (date + status set here) and tasks (dates and status from their sub-tasks).
// Drag the handle (mouse or touch) or use the arrows to reorder.
export default function TimelineItemsList({ items, errors, taskTags, onChange, onMove, onRemove, onMakeTask, onOpenTask }) {
    const nodes = useRef({});
    const [dragKey, setDragKey] = useState(null);

    const onPointerDown = (e, key) => {
        e.preventDefault();
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* not supported */ }
        setDragKey(key);
    };
    const onPointerMove = (e) => {
        if (!dragKey) return;
        const from = items.findIndex(i => i.key === dragKey);
        const prev = items[from - 1] && nodes.current[items[from - 1].key];
        const next = items[from + 1] && nodes.current[items[from + 1].key];
        if (prev) { const r = prev.getBoundingClientRect(); if (e.clientY < r.top + r.height / 2) { onMove(from, from - 1); return; } }
        if (next) { const r = next.getBoundingClientRect(); if (e.clientY > r.top + r.height / 2) onMove(from, from + 1); }
    };
    const endDrag = () => setDragKey(null);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {items.map((it, i) => {
                const isTask = it.kind === 'task';
                return (
                    <Box key={it.key} ref={(el) => { nodes.current[it.key] = el; }}
                        sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', border: '1px solid #e5e9ee', borderLeft: isTask ? `4px solid ${THEME_COLOR}` : '1px solid #e5e9ee', borderRadius: 2.5, p: 1, bgcolor: it.client_visible ? '#fff' : '#fafbfc', boxShadow: dragKey === it.key ? '0 10px 26px rgba(0,0,0,.18)' : '0 1px 2px rgba(16,40,32,.05)', transition: 'box-shadow .15s', position: 'relative', zIndex: dragKey === it.key ? 2 : 'auto' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Box onPointerDown={(e) => onPointerDown(e, it.key)} onPointerMove={onPointerMove} onPointerUp={endDrag} onPointerCancel={endDrag}
                                title="Drag to move" sx={{ cursor: dragKey ? 'grabbing' : 'grab', color: '#a3adb8', touchAction: 'none', display: 'flex', p: 0.5, borderRadius: 1, '&:hover': { bgcolor: '#f1f5f9', color: '#1f2933' } }}>
                                <DragIndicatorIcon fontSize="small" />
                            </Box>
                            <IconButton size="small" disabled={i === 0} onClick={() => onMove(i, i - 1)} aria-label="Move up"><ArrowUpwardIcon sx={{ fontSize: 16 }} /></IconButton>
                            <IconButton size="small" disabled={i === items.length - 1} onClick={() => onMove(i, i + 1)} aria-label="Move down"><ArrowDownwardIcon sx={{ fontSize: 16 }} /></IconButton>
                        </Box>
                        {isTask
                            ? <TaskFields item={it} error={typeof errors[it.key] === 'string' ? errors[it.key] : null} taskTags={taskTags} onChange={(p) => onChange(it.key, p)} onOpen={(subKey) => onOpenTask(it.key, subKey)} />
                            : <SimpleStepFields item={it} error={errors[it.key]} onChange={(p) => onChange(it.key, p)} onMakeTask={() => onMakeTask(it.key)} />}
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 'none' }}>
                            <Tooltip title="Show this step on the client's timeline">
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Checkbox size="small" checked={!!it.client_visible} onChange={(e) => onChange(it.key, { client_visible: e.target.checked })} sx={checkboxStyle} />
                                    <Typography sx={{ fontSize: '12px', color: '#6b7785', mr: 0.5 }}>Client</Typography>
                                </Box>
                            </Tooltip>
                            <Tooltip title={isTask ? 'Delete this task and its sub-tasks' : 'Remove step'}>
                                <IconButton size="small" color="error" onClick={() => onRemove(it.key)} aria-label="Remove"><DeleteIcon fontSize="small" /></IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                );
            })}
            {!items.length && <Typography sx={{ textAlign: 'center', color: '#6b7785', fontSize: '13px', py: 2 }}>No steps yet. Add a task or a simple step below.</Typography>}
        </Box>
    );
}
